// components/Reusablesteps/Entryreasonscreen.jsx
//
// Auto-resolves life stage from localStorage (age_group + maritalStatus).
// Shows entry reasons for that stage as MULTI-SELECT cards.
// Back button goes to the last basic-info step.
//
// Props:
//   onNext(payload)  — { reasons[], routes[], lifeStageKey, journeyTitle }
//   onBack()         — navigates back to the last step question

import React, { useState, useMemo } from "react";
import styles from "./Entryreasonscreen.module.css";
import StepperProgress from "./Stepperprogress";
import { getEntryReasonJourney, saveEntryReasons } from "./Resolvelifestage";

/* ── Route urgency → badge ──────────────────────────────────────── */
const getUrgencyBadge = (route = "") => {
  const r = route.toLowerCase();
  if (r.includes("urgent") || r.includes("triage") || r.includes("immediate") || r.includes("escalation"))
    return { label: "Urgent", color: "#EF4444", bg: "#FEF2F2" };
  if (r.includes("priority"))
    return { label: "Priority", color: "#F59E0B", bg: "#FFFBEB" };
  return null;
};

/* ── Life stage display metadata ─────────────────────────────────── */
const STAGE_META = {
  adolescence:        { label: "Adolescence Journey",              color: "#FFF0E8", dot: "#F97316" },
  youngAdult:         { label: "Young Adult / Reproductive",       color: "var(--ava-bg-primary-light)", dot: "#3B82F6" },
  matureReproductive: { label: "Preconception / Fertility",        color: "#F0FFF4", dot: "#22C55E" },
  perimenopause:      { label: "Perimenopause Journey",            color: "#FFF8E1", dot: "#EAB308" },
  menopause:          { label: "Menopause Journey",                color: "#FDF4FF", dot: "#A855F7" },
  postmenopause:      { label: "Postmenopause Journey",            color: "#F0F4FF", dot: "#6366F1" },
  geriatric:          { label: "Senior Women's Health Journey",    color: "#F5F5F5", dot: "#6B7280" },
};

/* ─────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────── */
const EntryReasonScreen = ({ onNext, onBack, currentStep, totalSteps }) => {
  const [selected, setSelected] = useState([]); // multi-select array

  /* Resolve journey once on mount */
  const { stageKey, journey, ageGroup, marital } = useMemo(
    () => getEntryReasonJourney(),
    []
  );

  const stageMeta = STAGE_META[stageKey] || STAGE_META.youngAdult;

  /* Toggle selection */
  const toggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  /* Continue */
  const handleNext = () => {
    if (selected.length === 0) return;
    saveEntryReasons(stageKey, selected, journey);
    const selectedOptions = journey.options.filter((o) => selected.includes(o.value));
    onNext({
      reasons:      selected,
      routes:       selectedOptions.map((o) => o.route),
      lifeStageKey: stageKey,
      journeyTitle: journey.journeyTitle,
      options:      selectedOptions,
    });
  };

  return (
    <div className={styles.wrapper}>
      {/* Progress dots */}
      {typeof currentStep === "number" && (
        <div className={styles.progress}>
          <StepperProgress current={currentStep} total={totalSteps} />
        </div>
      )}

      {/* Life stage badge */}
      <div
        className={styles.stageBadge}
        style={{ background: stageMeta.color }}
      >
        <span
          className={styles.stageDot}
          style={{ background: stageMeta.dot }}
        />
        <span className={`ava-text-caption ${styles.stageLabel}`}>
          {journey.journeyTitle}
        </span>
      </div>

      {/* Header */}
   <div className={styles.header}>
  <div className={styles.headerRow}>
    <div className={styles.headerLeft}>
      <h2 className={`ava-heading-md ${styles.title}`}>
        {journey.screenTitle}
      </h2>
      <p className={`ava-text-caption ${styles.subtitle}`}>
        Select all that apply — you can choose more than one
      </p>
    </div>
    {selected.length > 0 && (
      <span className={styles.countPill}>
        {selected.length} selected
      </span>
    )}
  </div>
</div>

      {/* Options list — MULTI-SELECT */}
      <div className={styles.list}>
        {journey.options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          const badge      = getUrgencyBadge(opt.route);

          return (
            <button
              key={opt.value}
              type="button"
              className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
              onClick={() => toggle(opt.value)}
            >
              {/* Left emoji */}
              {/* <span className={styles.cardEmoji}>{opt.emoji || "🔵"}</span> */}

              {/* Middle text */}
              <div className={styles.cardTop}>
                <span className={styles.cardEmoji}>{opt.emoji || "🔵"}</span>
                <span className={`ava-text-subheading ${styles.cardLabel}`}>
                  {opt.label}
                </span>
              </div>
              <span className={`ava-text-caption ${styles.cardDesc}`}>
                {opt.desc}
              </span>

              {/* Right: urgency badge + checkbox */}
              <div className={styles.cardRight}>
                {badge && (
                  <span
                    className={styles.badge}
                    style={{ color: badge.color, background: badge.bg }}
                  >
                    {badge.label}
                  </span>
                )}
                {/* Checkbox tick */}
                <span
                  className={`${styles.checkbox} ${isSelected ? styles.checkboxChecked : ""}`}
                >
                  {isSelected && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <polyline
                        points="1.5 5 4 7.5 8.5 2.5"
                        stroke="#fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selection count pill */}
      

      {/* Action buttons */}
   <div className="flex items-center justify-between w-full mt-1">
  {onBack && (
    <button
      type="button"
      className={styles.backBtn}
      onClick={onBack}
    >
      ← Back
    </button>
  )}
  <button
    className="btn-primary"
    disabled={selected.length === 0}
    onClick={handleNext}
    type="button"
  >
    Continue →
  </button>
</div>

      {/* <p className={`ava-text-caption ${styles.note}`}>
        Your information is private and secure 🔒
      </p> */}
    </div>
  );
};

export default EntryReasonScreen;